import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from 'src/app/_services/toaster.service';
import { Subscription } from 'rxjs';
import { CEUService, EventService, OrganizationService, UserService } from 'src/app/_services/generatedServices';
import { EventViewModel, InstructorViewModel, CourseEventStatus, OrganizationViewModel, ConferenceViewModel, PackageDropdownViewModel, ScheduleUserEventViewModel, OrganizationCertificationViewModel } from 'src/app/_models/generatedModels';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { Enums } from 'src/app/_models/generatedEnums';
import { DateTime } from 'luxon';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import * as moment from 'moment';
import 'moment-timezone';
import * as customBuild from 'src/ckCustomBuild/ckeditor';
import { ValidatorHelper } from 'src/app/_helpers/validationHelper';

@Component({
  selector: 'bxl-schedule-event',
  templateUrl: 'schedule-event.component.html',
})
export class ScheduleEventComponent implements OnInit {
  private routeSubscription: Subscription;
  eventId: any;
  submitComplete: Promise<{}> | undefined;
  event: EventViewModel;
  public initialized = false;
  public eventForm: FormGroup;
  instructors: InstructorViewModel[];
  tags: string[];
  uploadUrl: string;
  marketingSplash: string;
  ogSplash: string;
  ogTwitterSplash: string;
  sponsorOffAirImage: string;
  sponsorBugImage: string;
  public CourseEventStatus = CourseEventStatus;
  public CourseEventStatusEnum = Enums.CourseEventStatusEnum;
  orgs: OrganizationViewModel[];
  conferences: ConferenceViewModel[];
  packages: PackageDropdownViewModel[];
  certifications: OrganizationCertificationViewModel[];

  imageChangedEvent: any = '';
  croppedImage: any = '';
  initialCroppedImage: string;
  modalImage: any;

  public Editor:any = customBuild;

  htmlEditorConfig = {
    toolbar: {
      items: ['bold', 'italic', 'underline', 'link', 'alignment', 'outdent', 'indent', 'bulletedList', 'numberedList', '|', 'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', 'SourceEditing'],
      shouldNotGroupWhenFull: false,
    },
    fontSize: {
      options: [9, 11, 13, 'default', 17, 19, 21],
    },
    mediaEmbed: {
      previewsInData: true,
    },
  };

  constructor(public auth: AuthenticationService, private organizationService: OrganizationService, private ceuService: CEUService, private modalService: NgbModal, private userService: UserService, private route: ActivatedRoute, private router: Router, private eventData: EventService, private toastr: ToasterService, private fb: FormBuilder) {}

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  ngOnInit(): void {
    this.uploadUrl = '/api/uploadImage';

    this.routeSubscription = this.route.paramMap.subscribe((param) => {
      this.eventId = param.get('id');

      this.auth.fetchUserProfile().subscribe((authResult) => {
        if (this.eventId != null) {
          this.eventData.getUserScheduledEvent(this.eventId).subscribe((event) => {
            this.event = event;
            if (authResult.avatar) {
              this.userService.getImageAsBase64().subscribe((image) => {
                this.croppedImage = image.value;
                this.initialCroppedImage = image.value;
                this.populateDropdowns();
              });
            } else {
              this.populateDropdowns();
            }
          });
        } else {
          this.event = new EventViewModel();
          this.event.currencyCode = 'USD';
          this.event.instructorCanEdit = true;
          this.event.ceus = [];

          if (authResult.avatar) {
            this.userService.getImageAsBase64().subscribe((image) => {
              this.croppedImage = image.value;
              this.initialCroppedImage = image.value;
              this.populateDropdowns();
            });
          } else {
            this.populateDropdowns();
          }
        }
      });
    });
  }

  private populateDropdowns() {
    var certSubscription = this.event.restrictToOrganizationId == null ? this.ceuService.getCertificationsForPlatform() : this.ceuService.getCertificationsForOrganization(this.event.restrictToOrganizationId.toString());
    certSubscription.subscribe((certs) => {
      this.certifications = certs;
      this.setupForm();
    });
  }

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.modalImage = event.base64;
    console.log(this.initialCroppedImage === this.croppedImage);
  }

  imageLoaded() {
    console.log('loaded');
    // show cropper
  }

  loadImageFailed() {
    // show message
  }

  removeImage(index) {
    (<FormArray>this.eventForm.controls['images']).removeAt(index);
    console.log(index);
  }

  editImage(content) {
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title', size: 'lg', backdrop: 'static' }).result.then(
      (result) => {
        this.croppedImage = this.modalImage;
        console.log('here');
      },
      (reason) => {
        console.log('here2');
      }
    );
  }

  setupForm(): any {
    let eventDate = null;
    let endDate = null;

    if (this.event.startTime != null) {
      let tmpDate = DateTime.fromISO(this.event.startTime, { zone: this.event.timeZone });
      let tmpEndDate = DateTime.fromISO(this.event.endTime, { zone: this.event.timeZone });
      eventDate = new Date(tmpDate.year, tmpDate.month - 1, tmpDate.day, tmpDate.hour, tmpDate.minute, tmpDate.second);
      endDate = new Date(tmpEndDate.year, tmpEndDate.month - 1, tmpEndDate.day, tmpEndDate.hour, tmpEndDate.minute, tmpEndDate.second);
    }

    this.eventForm = this.fb.group({
      title: [this.event.title, [Validators.required]],
      avatar: [this.auth.user.avatar],
      eventDateTime: [eventDate, [Validators.required]],
      startTime: [eventDate, [Validators.required]],
      endTime: [endDate, [Validators.required]],
      firstName: [this.auth.user.firstName, [Validators.required]],
      lastName: [this.auth.user.lastName, [Validators.required]],
      credentials: [this.auth.user.credentials],
      certificantNumber: [this.auth.user.certificantNumber],
      email: [this.auth.user.email, [Validators.required]],
      alternateEmail: [this.auth.user.alternateEmail],
      phoneNumber: [this.auth.user.phoneNumber],
      biography: [this.auth.user.biography, [Validators.required]],
      price: [this.event.price, [Validators.required]],
      description: [this.event.description, [Validators.required]],
      timeZone: [this.event.timeZone, [Validators.required]],
      learningObjective1: [this.event.learningObjective1],
      learningObjective2: [this.event.learningObjective2],
      learningObjective3: [this.event.learningObjective3],
      ceus: this.fb.array([]),
    });
    this.loadCEUs();

    this.initialized = true;
  }

  loadCEUs() {
    let ceus = this.eventForm.get('ceus') as FormArray;

    this.certifications.forEach((cert) => {
      var ceu = this.event.ceus.find((x) => x.certificationBodyId == cert.certificationBodyId);
      var array = this.fb.group(
        {
          certificationBodyId: [cert.certificationBodyId],
          ceuCount: [ceu?.ceuCount, [Validators.required]],
          subType1CeuCount: [ceu?.subType1CEUCount, [Validators.required]],
          subType2CeuCount: [ceu?.subType2CEUCount, [Validators.required]],
          subType3CeuCount: [ceu?.subType3CEUCount, [Validators.required]],
        },
        { validators: [ValidatorHelper.totalCEUValidator()], updateOn: 'change' }
      );

      if (cert.certificationBody.subType1Name == null) {
        console.log('clearing validators');
        array.controls.subType1CeuCount.clearValidators();
      }

      if (cert.certificationBody.subType2Name == null) {
        console.log('clearing validators');
        array.controls.subType2CeuCount.clearValidators();
      }

      if (cert.certificationBody.subType3Name == null) {
        console.log('clearing validators');
        array.controls.subType3CeuCount.clearValidators();
      }

      ceus.push(array);
    });
  }
  navigateBack() {
    this.router.navigate(['/account/live-events'], { relativeTo: this.route.parent });
  }

  public onSave() {
    if (!this.eventForm.valid) {
      this.eventForm.markAllControlsDirty();
      this.toastr.error('Please fill out all required fields', 'Error');
      return;
    }

    this.submitComplete = new Promise((resetButton: any, reject) => {
      const formData: ScheduleUserEventViewModel = Object.assign({}, this.event, this.eventForm.getRawValue());
      let timezone = this.eventForm.controls.timeZone.value;
      let startTime = new Date(this.eventForm.controls.startTime.value);
      let endTime = new Date(this.eventForm.controls.endTime.value);
      let theDate = moment(this.eventForm.controls.eventDateTime.value).format('YYYY-MM-DD');
      let theStartTime = startTime.toTimeString().substr(0, 8);
      let theEndTime = endTime.toTimeString().substr(0, 8);
      let finalStartTime = DateTime.fromISO(theDate + 'T' + theStartTime, { zone: timezone });
      let finalEndTime = DateTime.fromISO(theDate + 'T' + theEndTime, { zone: timezone });

      formData.startTime = finalStartTime.toJSDate();
      formData.endTime = finalEndTime.toJSDate();

      if (this.initialCroppedImage !== this.croppedImage) {
        formData.base64Image = this.croppedImage;
      } else {
        formData.base64Image = null;
      }

      if (this.event.id != null) {
        this.update(formData, resetButton);
      } else {
        this.save(formData, resetButton);
      }
    });
  }

  save(formData: ScheduleUserEventViewModel, resetButton: () => any) {
    this.eventData.scheduleUserEvent(formData).subscribe(
      (job) => {
        this.navigateBack();
        this.toastr.success('Event Saved', 'Success');
        resetButton();
      },
      (error) => resetButton()
    );
  }

  update(formData: ScheduleUserEventViewModel, resetButton: () => any) {
    this.eventData.updateUserEvent(this.eventId, formData).subscribe(
      (job) => {
        this.navigateBack();
        this.toastr.success('Event Updated', 'Success');
        resetButton();
      },
      (error) => resetButton()
    );
  }

  onCancel() {
    if (this.eventForm.dirty) {
      this.toastr.confirmDialog('Are you sure?', 'Discard Changes').subscribe((result) => {
        if (result) {
          this.navigateBack();
        }
      });
    } else {
      this.navigateBack();
    }
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }
}
